import { Head } from "@inertiajs/react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import AppearanceTabs from "@/components/appearance-tabs"
import HeadingSmall from "@/components/heading-small"
import LanguageSection from "@/components/settings/language-section"
import { Separator } from "@/components/ui/separator"
import { useCurrentContext } from "@/hooks/use-current-context"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { DEFAULT_LOCALE } from "@/lib/i18n/client"
import { settingsAppearancePath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

export default function Appearance() {
  const { t } = useTranslation()
  const { currentUser } = useCurrentContext()

  const breadcrumbs: BreadcrumbItem[] = useMemo(
    () => [
      {
        title: t("settings.appearance.breadcrumb"),
        href: settingsAppearancePath(),
      },
    ],
    [t],
  )

  // OKS-96 — Default to the project locale on the rare pre-hydration
  // paths where currentUser is somehow missing. The settings layout
  // itself is auth-gated, so in practice this fallback is defensive.
  const currentLocale = currentUser?.locale ?? DEFAULT_LOCALE

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title={t("settings.appearance.title")}
            description={t("settings.appearance.description")}
          />
          <AppearanceTabs />

          <Separator />

          <LanguageSection currentLocale={currentLocale} />
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
